import { Badge } from '@wundo/ui';
import type { JourneyManagerReachability } from '@wundo/shared/journey.ts';
import type { NotificationDeliveryReason } from '@wundo/shared/notificationChannel.ts';

type Channel = Exclude<JourneyManagerReachability['channel'], 'none'>;

const CHANNEL_LABELS: Record<Channel, string> = {
  msteams: 'Teams',
  slack: 'Slack',
  email: 'Email',
};

/* Why the reminders reach nobody, in the words of the situation. The three channel reasons never get here. */
const UNREACHABLE_LABELS: Record<Exclude<NotificationDeliveryReason, Channel>, string> = {
  no_teams_chat: 'no Teams chat with Wundo yet',
  no_slack_chat: 'no Slack DM with Wundo yet',
  no_webapp: 'email needs the web app, which is off for this organisation',
  teams_only: 'only sent in Teams, and they are on another channel',
  teams_or_email_only: 'only sent in Teams or by email, and they are on Slack',
  no_email_address: 'no email address',
  opted_out: 'unsubscribed from Wundo emails',
  channel_none: 'notification channel set to none',
};

/** Where the manager's journey reminders go, or why they would reach nobody. */
export function ManagerReachabilityBadge({
  reachability,
}: {
  reachability: JourneyManagerReachability;
}) {
  const { channel, reason } = reachability;

  if (channel !== 'none') return <Badge tone='neutral'>{CHANNEL_LABELS[channel]}</Badge>;

  const why =
    reason === 'msteams' || reason === 'slack' || reason === 'email'
      ? 'no channel'
      : UNREACHABLE_LABELS[reason];

  return <Badge tone='warning'>Unreachable: {why}</Badge>;
}
